import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export type SortField =
  | "fileName"
  | "status"
  | "confidence"
  | "uploadedAt"
  | "documentType"
  | "proNumber";
export type SortDirection = "asc" | "desc";

export interface SortState {
  field: SortField;
  direction: SortDirection;
}

export interface UseSortReturn {
  sortField: SortField;
  sortDirection: SortDirection;
  setSort: (field: SortField) => void;
  clearSort: () => void;
}

const DEFAULT_SORT_FIELD: SortField = "uploadedAt";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

/**
 * Hook to manage sort state in URL search params
 * Persists sort across navigation and page refreshes
 */
export function useSort(): UseSortReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive sort state from URL
  const sortField = (searchParams.get("sortBy") as SortField) || DEFAULT_SORT_FIELD;
  const sortDirection = (searchParams.get("sortDir") as SortDirection) || DEFAULT_SORT_DIRECTION;

  // Toggle sort - clicking same column toggles direction, different column sets desc
  const setSort = useCallback(
    (field: SortField) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          if (field === sortField) {
            // Toggle direction
            const newDirection = sortDirection === "asc" ? "desc" : "asc";
            if (field === DEFAULT_SORT_FIELD && newDirection === DEFAULT_SORT_DIRECTION) {
              // Reset to default - remove params for clean URL
              params.delete("sortBy");
              params.delete("sortDir");
            } else {
              params.set("sortBy", field);
              params.set("sortDir", newDirection);
            }
          } else {
            // New column - default to desc (most recent/highest first)
            params.set("sortBy", field);
            params.set("sortDir", "desc");
          }

          return params;
        },
        { replace: true }
      );
    },
    [sortField, sortDirection, setSearchParams]
  );

  const clearSort = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("sortBy");
        params.delete("sortDir");
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return {
    sortField,
    sortDirection,
    setSort,
    clearSort,
  };
}

/**
 * Map frontend field names to backend field names
 */
export function mapSortFieldToBackend(field: SortField): string {
  const fieldMap: Record<SortField, string> = {
    fileName: "file_name",
    status: "status_id",
    confidence: "confidence",
    uploadedAt: "created_on",
    documentType: "document_type",
    proNumber: "pro_number",
  };
  return fieldMap[field];
}
